import type { ColumnFiltersState } from "@tanstack/react-table";
import type { ReactNode } from "react";

import type { PermissionBit } from "../../../../api";
import type { DataModel, SelectionMode } from "../../../../models/data-model";
import type { EnumLabel } from "../../fields/utils";
import type { CellProps } from "../cells/cell";
import type { ColumnFilterSpec, FilterField } from "./filter-toolbar/types";
import type { DataTableInstance } from "./table-features";

export type { FilterField };

interface ColumnBase<T> {
  header: ReactNode;
  /**
   * Name to use for this column where the `header` node itself isn't a good
   * label (the edit dialog's form labels, the filter toolbar) — e.g. a header
   * carrying an icon or a tooltip. Both fall back to `header` when omitted, so
   * only columns whose header renders more than its own name need it.
   */
  label?: string;
  attr?: keyof T;
  editable?: boolean | ((item: T) => boolean);
  sort?: boolean;
  /**
   * Attribute to fall back to when two rows compare equal on this column, so
   * the order within a tie is meaningful instead of API order — e.g. a date
   * column breaking same-day ties on a full timestamp. Sorted in the same
   * direction as the column itself. Only applies when `sort` is on.
   */
  sortTiebreakBy?: keyof T;
  initSort?: "asc" | "desc";
  filter?: ColumnFilterSpec<T>;
  requiredPermission?: PermissionBit;
  sticky?: boolean | "right" | "left";
  /**
   * Register the column for its `filter` only — it never renders. Lets a
   * filter (typically one with a custom `predicate`) appear in the toolbar
   * without adding a visible column.
   */
  hidden?: boolean;
  cell?: (props: CellProps<T>) => ReactNode;
  /**
   * For dialog edit mode: render this column's custom `cell` (bound to the
   * edit draft via `patch`) inside the edit form. Use for columns whose
   * editing UI is a custom cell rather than a built-in `type` field.
   */
  editInDialog?: boolean;
  /** Pixel width for this column. Defaults to 150 when omitted. */
  width?: number;
  /** Horizontal alignment of the column's header and cells. Defaults to "center". */
  align?: "left" | "center" | "right";
  /** Extra classes applied to this column's body cells (the `td` elements). */
  className?: string;
  /**
   * Groups this column under a shared header above the column headers. Adjacent
   * columns declaring the same group are spanned by one group cell, so group
   * membership follows column order — a group interrupted by another group (or
   * by an ungrouped column) renders as two separate spans. Columns without a
   * group get an empty cell in the group row.
   *
   * Caveat: with `fillHeight` both header rows stick to the same offset and
   * would overlap, so grouped columns and `fillHeight` don't currently mix.
   */
  group?: string;
  /**
   * Show a copy-to-clipboard icon next to the cell's value (e.g. email, phone).
   * Only applies to the static, non-editing display and when the value is
   * non-empty.
   */
  copyable?: boolean;
}

export interface ColumnUnknown<T> extends ColumnBase<T> {
  type?: never;
}

export interface ColumnString<T> extends ColumnBase<T> {
  type: "string";
  /**
   * Wrap long content across multiple lines, clamped to a fixed line count so
   * every row keeps the same height (short content reserves the same space).
   * `true` clamps to 2 lines; a number sets the cap. Editing still renders an
   * autosizing textarea.
   */
  multiline?: boolean | number;
}

export interface ColumnRichText<T> extends ColumnBase<T> {
  type: "richtext";
}

export interface ColumnEnum<T> extends ColumnBase<T> {
  type: "enum";
  values: Record<string, EnumLabel> | any[];
}

export interface ColumnNumber<T> extends ColumnBase<T> {
  type: "number";
  precision?: number;
}

export interface ColumnArray<T> extends ColumnBase<T> {
  type: "array";
  values?: Record<string, EnumLabel> | any[];
}

export interface ColumnDate<T> extends ColumnBase<T> {
  type: "datetime" | "date";
}

export interface ColumnBoolean<T> extends ColumnBase<T> {
  type: "boolean";
}

export type EditMode = "row" | "dialog" | "cell";

/** Column definition for DataTable, also consumed by the shared Cell and EditRowDialog renderers. */
export type Column<T> =
  | ColumnUnknown<T>
  | ColumnString<T>
  | ColumnRichText<T>
  | ColumnEnum<T>
  | ColumnNumber<T>
  | ColumnArray<T>
  | ColumnDate<T>
  | ColumnBoolean<T>;

export interface DataTableProps<T extends object, K extends keyof T = keyof T> {
  model: DataModel<T>;
  columns: Array<Column<T> | false | null | undefined>;
  itemKeyField: K;
  rowsPerPage?: number;
  persistState?: boolean;
  /**
   * Scopes persisted state to a sub-context of the route (e.g. an active tab
   * id). When this changes, also pass `key={persistStateKey}` on DataTable
   * so a fresh table instance hydrates from the matching storage entry.
   *
   * Caveat: only sorting/filters are scoped by this key (they live in
   * sessionStorage). Pagination (`page`/`pageSize`) lives in shared URL query
   * params and is NOT scoped — when switching sub-contexts, the caller must
   * also delete `page`/`pageSize` from the URL, otherwise the previous
   * sub-context's page leaks into the next one. See `patients-route.tsx` for
   * the canonical tab-switch handler.
   */
  persistStateKey?: string;
  editMode?: EditMode;
  isLoading?: boolean;
  /**
   * Fill the parent and scroll only the table rows vertically; the filter
   * toolbar, column headers, and pagination stay visible. The parent must be
   * a height-constrained flex column (the table becomes a flex item — sizing
   * is flex-based rather than percentage-based, since percentage heights
   * don't resolve under `max-height`-capped ancestors).
   */
  fillHeight?: boolean;
  /**
   * Noun for the toolbar's "X of Y" item-count text, pluralized for `count`.
   * A function rather than a singular/plural pair because "which form for
   * which number" is a property of the language, not of English: Slovak needs
   * three forms (1 / 2–4 / 5+) and picks between them by CLDR category. Build
   * it with the `plural` macro so the catalogs carry every form the locale
   * needs — `(count) => t`${plural(count, { one: "row", other: "rows" })}``.
   */
  filterNoun?: (count: number) => string;
  /** Custom React node rendered on the left side of the filter toolbar (e.g. an "Add" button). */
  filterActions?: ReactNode;
  /** Initial column filter state — used when persistState is false (e.g. modals). */
  initialColumnFilters?: ColumnFiltersState;
  /**
   * External values read by filter `predicate`s beyond the row itself (e.g. a
   * MobX store that loads after the rows). TanStack's filtered row model is
   * memoized on the rows and the filter state, so it can't see such updates —
   * declare them here (React-deps style, stable length) and active filters
   * re-evaluate whenever one changes.
   */
  filterDeps?: unknown[];
  canSelect?: SelectionMode;
  /**
   * Explanatory hint for what row selection does, shown as an info-icon
   * tooltip beside the select-all checkbox in the column header. Only rendered
   * in multi-select mode.
   */
  selectionHint?: ReactNode;
  /**
   * Predicate deciding whether a row's built-in selection checkbox/radio is
   * disabled (e.g. rows selected implicitly by another selection). Only applies
   * when `canSelect` is set.
   */
  selectDisabled?: (item: T) => boolean;
  readOnly?: boolean;
  preselected?: T[K][];
  onSelect?: (selected: T[K][]) => void;
  onAdd?: (added: Partial<T>) => void | Promise<unknown>;
  onEdit?: (patch: Partial<T>, row: T) => void | Promise<unknown>;
  onDelete?: (row: T) => void | Promise<unknown>;
  /**
   * Providing this opts the table into multi-row edit mode: per-row Edit
   * toggles into a shared draft set, and Save N commits via this callback.
   * Receives one entry per dirty row; may return `{ failedIds }` to keep
   * drafts in place for rows whose save failed, so the user can retry.
   */
  onMultiEdit?: (
    patches: Array<{ id: number; patch: Partial<T> }>,
  ) => void | Promise<void | { failedIds?: unknown[] } | undefined>;
  patchAlwaysIncludeKeys?: Extract<keyof T, string>[];
  renderEditDialog?: (props: {
    item: T;
    onClose: () => void;
    onSave: (patch: Record<string, unknown>) => Promise<void>;
  }) => ReactNode;
  /**
   * Content for the table's `<tfoot>` (must render `<tr>` rows). Pass a
   * function to derive it from the table instance, e.g. totals over
   * `getFilteredRowModel()` that follow the active filters.
   */
  foot?: ReactNode | ((table: DataTableInstance<T>) => ReactNode);
  verticalAlign?: "top";
  /**
   * Extra classes for a row's `tr`, derived from the item — e.g. dimming rows
   * that are inactive for the current context. Purely presentational; it can't
   * change what the row renders.
   */
  rowClassName?: (item: T) => string | undefined;
}

export function getColumnId<T>(col: Column<T>, index: number): string {
  return String(col.attr ?? `col-${index}`);
}

/**
 * Expands a column's inline filter spec into a full FilterField, deriving
 * `id`/`attr`/`label` from the column. Returns undefined for non-filterable
 * columns (no spec or no attr).
 */
export function filterFieldForColumn<T>(
  col: Column<T>,
): FilterField<T> | undefined {
  const spec = col.filter;
  if (!spec || col.attr == null) return undefined;
  const id = String(col.attr);
  // The header is rendered as-is (it's usually a `<Trans>`), so a translated
  // column keeps a translated filter label without restating the string.
  const label = spec.label ?? col.label ?? col.header ?? id;
  return { ...spec, id, attr: col.attr, label };
}

declare module "@tanstack/react-table" {
  // Still declaration merging rather than v9's per-table `columnMeta` slot:
  // that slot takes a fixed type, and this one has to stay generic over TData
  // to keep `meta.column` tied to the row type.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TFeatures, TData, TValue> {
    column?: Column<TData & object>;
    /** First column of a `group` — draws the group divider down the table. */
    groupStart?: boolean;
  }
  // The `FilterFns` interface that used to be augmented here is gone: v9
  // derives the valid `filterFn` strings from the registry in
  // `dataTableFeatures`, so registering a fn is now what types it.
}
